import { parseISO } from 'date-fns'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { addCalendarDays, type Region, toWallClockDate } from '@snootsvet/shared/portal'

import { Button } from '@/customer/components/ui/button'
import { useIsMobile } from '@/customer/hooks/use-is-mobile'
import { cn } from '@/customer/lib/utils'

/**
 * Per-day slot count above which the picker collapses the tail
 * behind a "+ N more times" toggle. Sized to fit ~4 rows of the
 * mobile 3-column grid, which is the tighter of the two layouts;
 * desktop shows 12 vertical entries per column, which also feels
 * about right before a column starts to dominate the page. State
 * is per-day and resets when the week changes (day sections
 * unmount on nav).
 */
const SLOT_TRUNCATION_LIMIT = 12

type Slot = { start: string; end: string }

/**
 * Presentational weekly appointment-slot picker.
 *
 * Extracted from the tokened booking page; also used by the portal
 * booking flow. The component is stateless with respect to fetching -
 * the parent owns `slots`, `weekStart`, and the selected slot, and
 * receives a callback whenever the user pages between weeks or picks
 * a time.
 *
 * Two internal layouts:
 *
 * - `DesktopWeekSlotPicker` - 7-column grid, one column per day. The
 *   canonical booking-week experience when there's room.
 * - `MobileWeekSlotPicker` - vertical list, one section per day with
 *   a 3-column slot grid inside. Days with no availability keep a
 *   small "No availability" line so the week's rhythm survives sparse
 *   weeks.
 *
 * Layout is chosen via `useIsMobile()` (Tailwind `md` breakpoint).
 * We deliberately don't use CSS `hidden md:block` toggles because the
 * two layouts differ enough (interaction model, DOM structure) that
 * mounting both would double the state / event surface. The hook
 * returns `undefined` for one frame on cold load; we short-circuit
 * to `null` in that window to avoid a wrong-layout flash.
 *
 * Time handling: the week grid is addressed entirely by YYYY-MM-DD
 * calendar dates in the clinic's timezone. `weekStart` is one, and so
 * is every column key. Slots are instants, so the day a slot belongs
 * to is `toWallClockDate(slot, timezone)` - *not* the date in its ISO
 * string, since 20:00 in New York is already tomorrow in UTC.
 *
 * Keeping the grid in calendar-date space is what stops the columns
 * drifting for clinics behind UTC. Don't reintroduce `Date`s here:
 * formatting a midnight instant in the clinic's timezone is what used
 * to render 00:00Z as 20:00 the previous day and shift every column
 * label back a day. Slot *times* are of course still rendered in
 * `timezone`.
 *
 * Past-slot filtering is a backend responsibility - the picker
 * renders whatever the parent passes. See `/public/available-slots`
 * (and the future `/portal/appointments/available-slots`) for the
 * authoritative filter.
 */
export type WeekSlotPickerProps = {
  /** Available intervals to render within the visible week. */
  slots: Slot[] | undefined
  selectedSlot: string | null
  onSelectSlot: (slot: string | null) => void
  /** Monday of the visible week, as a YYYY-MM-DD calendar date. */
  weekStart: string
  onWeekChange: (nextWeekStart: string) => void
  /**
   * Earliest pageable week, as a YYYY-MM-DD calendar date. If omitted,
   * defaults to the Monday of the clinic's current week (i.e. can't
   * page into the past).
   */
  minWeekStart?: string
  /** IANA timezone used to format day + slot labels. Sourced from the clinic. */
  timezone: string
  /**
   * Region driving the locale + hour convention for day and slot
   * labels. Portal callers pass the current member's region; the
   * non-authed public booking flow passes the clinic's region.
   */
  region: Region
  loading?: boolean
  /**
   * Overrides the clock behind the today / past-day markers. Stories
   * and tests pin it so their output doesn't drift with the real date;
   * production omits it.
   */
  now?: Date
  className?: string
}

/**
 * Returns the Monday of the week containing the given YYYY-MM-DD
 * calendar date, as YYYY-MM-DD.
 *
 * Callers should pass the clinic's current date - `toWallClockDate(new
 * Date(), timezone)` - so the week is anchored where the clinic is,
 * not where the browser happens to be.
 */
export function getMondayOfWeek(date: string): string {
  const weekday = new Date(`${date}T00:00:00.000Z`).getUTCDay()
  return addCalendarDays(date, weekday === 0 ? -6 : 1 - weekday)
}

/** One column (desktop) / section (mobile) of the visible week. */
type WeekDayViewModel = {
  /** Clinic-local calendar date, YYYY-MM-DD. Also the React key. */
  dateStr: string
  /** Short weekday name, e.g. "Mon". */
  weekdayLabel: string
  /** Day of month, e.g. "24". */
  dayLabel: string
  /** Full day heading used by the mobile layout, e.g. "Mon 24 Aug". */
  longLabel: string
  isToday: boolean
  isPast: boolean
  /** Available intervals falling on this clinic-local day, in the order given. */
  slots: Slot[]
}

/**
 * Builds the week grid: seven clinic-local days, each carrying its own
 * labels and the slots that fall on it. Derived state shared by both
 * layouts - a plain function rather than a hook, since the picker has
 * no lifecycle beyond rendering parent-owned props.
 *
 * Exported for unit testing.
 */
export function getWeekViewModel({
  slots = [],
  weekStart,
  minWeekStart,
  timezone,
  region,
  now = new Date(),
}: Pick<
  WeekSlotPickerProps,
  'slots' | 'weekStart' | 'minWeekStart' | 'timezone' | 'region' | 'now'
>): {
  canGoPrev: boolean
  weekDays: WeekDayViewModel[]
  weekLabel: string
} {
  // "Today" is a clinic-local question: at 21:00 in New York the UTC
  // date has already rolled over, and the highlight would land on
  // tomorrow.
  const todayStr = toWallClockDate(now, timezone)
  const canGoPrev = weekStart > (minWeekStart ?? getMondayOfWeek(todayStr))

  // Bucket slots by the clinic-local day they fall on. Slots outside
  // the visible week simply never match a column and drop out, so the
  // parent may pass a superset (e.g. a longer range) without the
  // picker having to care.
  //
  // Deliberately a loop rather than `Object.groupBy`: this bundle is
  // customer-facing and Vite's default target is Safari 16, while
  // `Object.groupBy` needs Safari 17.4. It's a runtime built-in, so
  // esbuild won't downlevel it and nothing here polyfills it - an iOS
  // 16 member would get a blank page.
  const slotsByDate: Record<string, Slot[]> = {}
  for (const slot of slots) {
    const date = toWallClockDate(new Date(slot.start), timezone)
    slotsByDate[date] ??= []
    slotsByDate[date].push(slot)
  }

  const weekDays = Array.from({ length: 7 }, (_, i): WeekDayViewModel => {
    const dateStr = addCalendarDays(weekStart, i)
    // Local midnight, so the labels render this calendar date whatever
    // timezone the browser is in - see date-fns `parseISO`.
    const day = parseISO(dateStr)
    return {
      dateStr,
      weekdayLabel: day.toLocaleDateString(region.locale, { weekday: 'short' }),
      dayLabel: day.toLocaleDateString(region.locale, { day: 'numeric' }),
      longLabel: day.toLocaleDateString(region.locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }),
      isToday: dateStr === todayStr,
      isPast: dateStr < todayStr,
      slots: slotsByDate[dateStr] ?? [],
    }
  })

  const weekLabel = `${parseISO(weekStart).toLocaleDateString(region.locale, {
    day: 'numeric',
    month: 'short',
  })} – ${parseISO(addCalendarDays(weekStart, 6)).toLocaleDateString(region.locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`

  return { canGoPrev, weekDays, weekLabel }
}

export function WeekSlotPicker(props: WeekSlotPickerProps) {
  const isMobile = useIsMobile()

  // First-render null-guard avoids the wrong-layout flash when the
  // hook hasn't measured the viewport yet. See `useIsMobile` for
  // the underlying rationale.
  if (isMobile === undefined) return null

  return isMobile ? <MobileWeekSlotPicker {...props} /> : <DesktopWeekSlotPicker {...props} />
}

// ─── Shared helpers ────────────────────────────────────────────────

/**
 * Week nav row with prev / next chevrons and a range label. Shared
 * between the two layouts so page-navigation looks identical.
 */
function WeekNavHeader({
  weekStart,
  weekLabel,
  canGoPrev,
  onWeekChange,
  onSelectSlot,
}: {
  weekStart: string
  weekLabel: string
  canGoPrev: boolean
  onWeekChange: (next: string) => void
  onSelectSlot: (slot: string | null) => void
}) {
  const [startLabel, endLabel] = weekLabel.split(' – ')

  return (
    <div className="flex items-center justify-between gap-2">
      <Button
        type="button"
        variant="ghost"
        className="h-12 flex-col gap-1 px-2 normal-case 2xs:flex-row"
        disabled={!canGoPrev}
        onClick={() => {
          onWeekChange(addCalendarDays(weekStart, -7))
          onSelectSlot(null)
        }}
        aria-label="Previous week"
      >
        <ChevronLeft />
        <span className="text-button-sm">Prev</span>
      </Button>
      <span className="text-button-sm text-center 2xs:whitespace-nowrap">
        <span className="whitespace-nowrap">{startLabel} –</span> <br className="2xs:hidden" />
        <span className="whitespace-nowrap">{endLabel}</span>
      </span>
      <Button
        type="button"
        variant="ghost"
        className="h-12 flex-col gap-1 px-2 normal-case 2xs:flex-row"
        onClick={() => {
          onWeekChange(addCalendarDays(weekStart, 7))
          onSelectSlot(null)
        }}
        aria-label="Next week"
      >
        <ChevronRight className="2xs:order-last" />
        <span className="text-button-sm">Next</span>
      </Button>
    </div>
  )
}

function EmptyWeekState({
  weekStart,
  onWeekChange,
  onSelectSlot,
}: Pick<WeekSlotPickerProps, 'weekStart' | 'onWeekChange' | 'onSelectSlot'>) {
  return (
    <div className="flex min-h-32 flex-col items-center justify-center gap-3 py-6 text-center">
      <p className="text-label-md font-bold">No appointments available this week</p>
      <Button
        type="button"
        size="sm"
        onClick={() => {
          onWeekChange(addCalendarDays(weekStart, 7))
          onSelectSlot(null)
        }}
      >
        View following week <ChevronRight />
      </Button>
    </div>
  )
}

function LoadingWeekState() {
  return (
    <div
      role="status"
      className="flex min-h-32 flex-col items-center justify-center gap-3 py-6 text-center"
    >
      <Loader2 aria-hidden className="size-6 animate-spin text-muted-foreground" />
      <p className="text-label-md">Loading appointment times…</p>
    </div>
  )
}

/**
 * Renders a day's slots inside `containerClassName` (a flex-col for
 * desktop, a 3-col grid for mobile), with a "+ N more times" toggle
 * when the list exceeds `SLOT_TRUNCATION_LIMIT`. Expanded state is
 * local to the component - navigating between weeks unmounts the
 * day section, so state naturally resets on nav.
 */
function DaySlotList({
  slots,
  selectedSlot,
  onSelectSlot,
  timezone,
  region,
  containerClassName,
}: {
  slots: Slot[]
  selectedSlot: string | null
  onSelectSlot: (slot: string | null) => void
  timezone: string
  region: Region
  containerClassName: string
}) {
  const [expanded, setExpanded] = useState(false)
  const isTruncated = !expanded && slots.length > SLOT_TRUNCATION_LIMIT
  // Reserve the last visible cell for the "+ N more" toggle so the
  // row/column count matches a full non-truncated view. E.g. mobile
  // 3-col grid: 11 slot cells + 1 toggle cell = 4 clean rows of 3.
  const visible = isTruncated ? slots.slice(0, SLOT_TRUNCATION_LIMIT - 1) : slots
  const hiddenCount = slots.length - visible.length

  return (
    <div className={containerClassName}>
      {visible.map((slot) => (
        <SlotButton
          key={slot.start}
          slot={slot.start}
          isSelected={selectedSlot === slot.start}
          timezone={timezone}
          region={region}
          onClick={() => onSelectSlot(selectedSlot === slot.start ? null : slot.start)}
        />
      ))}
      {hiddenCount > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="cursor-pointer text-center font-sans text-xs font-bold text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          + {hiddenCount} more
        </button>
      ) : null}
    </div>
  )
}

/** Time-button used by both layouts. */
function SlotButton({
  slot,
  isSelected,
  timezone,
  region,
  onClick,
}: {
  slot: string
  isSelected: boolean
  timezone: string
  region: Region
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border-[1.5px] px-1 py-1 text-xs font-bold transition-colors',
        isSelected
          ? 'border-foreground bg-primary text-foreground'
          : 'border-border bg-muted/25 hover:border-foreground hover:bg-primary/50'
      )}
    >
      {new Date(slot).toLocaleTimeString(region.locale, {
        hour: region.hour12 ? 'numeric' : '2-digit',
        minute: '2-digit',
        hour12: region.hour12,
        timeZone: timezone,
      })}
    </button>
  )
}

// ─── Desktop: 7-column grid ────────────────────────────────────────

function DesktopWeekSlotPicker({
  slots,
  selectedSlot,
  onSelectSlot,
  weekStart,
  onWeekChange,
  minWeekStart,
  timezone,
  region,
  loading = false,
  now,
  className,
}: WeekSlotPickerProps) {
  const { canGoPrev, weekDays, weekLabel } = getWeekViewModel({
    slots,
    weekStart,
    minWeekStart,
    timezone,
    region,
    now,
  })

  const isEmpty = slots !== undefined && weekDays.every((day) => day.slots.length === 0)

  return (
    <div
      className={cn(
        loading || isEmpty
          ? 'rounded-xl border border-border bg-muted/25 p-4 sm:p-6'
          : 'flex flex-col gap-3',
        className
      )}
    >
      <WeekNavHeader
        weekStart={weekStart}
        weekLabel={weekLabel}
        canGoPrev={canGoPrev}
        onWeekChange={onWeekChange}
        onSelectSlot={onSelectSlot}
      />

      {loading ? (
        <LoadingWeekState />
      ) : slots === undefined ? null : isEmpty ? (
        <EmptyWeekState
          weekStart={weekStart}
          onWeekChange={onWeekChange}
          onSelectSlot={onSelectSlot}
        />
      ) : (
        <div className="flex flex-col gap-1">
          {/* Day headers. Today's column gets a small yellow underline
              accent to anchor "now" in the week without adding a full
              row of colour. */}
          <div className="flex gap-1 pb-1 border-b-2 border-foreground">
            {weekDays.map((day) => (
              <div key={day.dateStr} className="flex flex-1 min-w-0 flex-col items-center gap-0.5">
                <p
                  className={cn(
                    'text-button-sm',
                    day.isPast && 'text-muted-foreground line-through'
                  )}
                >
                  {day.weekdayLabel}
                </p>
                <p
                  className={cn(
                    'text-fine-print text-muted-foreground',
                    day.isPast && 'line-through'
                  )}
                >
                  {day.dayLabel}
                </p>
                <span
                  className={cn(
                    'mt-0.5 h-1 w-4 rounded-full bg-yellow',
                    !day.isToday && 'invisible'
                  )}
                  aria-hidden
                />
              </div>
            ))}
          </div>

          <div className="flex gap-1 pt-1">
            {weekDays.map((day) => (
              <div key={day.dateStr} className="flex flex-1 min-w-0 flex-col gap-1">
                {day.slots.length === 0 ? (
                  <p className="text-fine-print text-muted-foreground text-center py-2">–</p>
                ) : (
                  <DaySlotList
                    slots={day.slots}
                    selectedSlot={selectedSlot}
                    onSelectSlot={onSelectSlot}
                    timezone={timezone}
                    region={region}
                    containerClassName="flex flex-col gap-1"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Mobile: vertical day list ─────────────────────────────────────

function MobileWeekSlotPicker({
  slots,
  selectedSlot,
  onSelectSlot,
  weekStart,
  onWeekChange,
  minWeekStart,
  timezone,
  region,
  loading = false,
  now,
  className,
}: WeekSlotPickerProps) {
  const { canGoPrev, weekDays, weekLabel } = getWeekViewModel({
    slots,
    weekStart,
    minWeekStart,
    timezone,
    region,
    now,
  })

  const isEmpty = slots !== undefined && weekDays.every((day) => day.slots.length === 0)

  return (
    <div
      className={cn(
        loading || isEmpty
          ? 'rounded-xl border border-border bg-muted/25 p-4 sm:p-6'
          : 'flex flex-col gap-4',
        className
      )}
    >
      <WeekNavHeader
        weekStart={weekStart}
        weekLabel={weekLabel}
        canGoPrev={canGoPrev}
        onWeekChange={onWeekChange}
        onSelectSlot={onSelectSlot}
      />

      {loading ? (
        <LoadingWeekState />
      ) : slots === undefined ? null : isEmpty ? (
        <EmptyWeekState
          weekStart={weekStart}
          onWeekChange={onWeekChange}
          onSelectSlot={onSelectSlot}
        />
      ) : (
        <ul className="flex flex-col gap-5">
          {weekDays.map((day) => (
            <li key={day.dateStr} className="flex flex-col gap-2">
              <div className="flex items-center gap-2 border-b border-border pb-1">
                <p
                  className={cn(
                    'text-label-md',
                    day.isPast && 'text-muted-foreground line-through'
                  )}
                >
                  {day.longLabel}
                </p>
                {day.isToday ? (
                  <span className="rounded-full bg-yellow px-2 py-0.5 text-fine-print">Today</span>
                ) : null}
              </div>
              {day.slots.length === 0 ? (
                <p className="text-label-md text-muted-foreground">No availability</p>
              ) : (
                <DaySlotList
                  slots={day.slots}
                  selectedSlot={selectedSlot}
                  onSelectSlot={onSelectSlot}
                  timezone={timezone}
                  region={region}
                  containerClassName="grid grid-cols-3 gap-2"
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
